import React from 'react';
import { Alert, Box, Button } from '@mui/material';

/**
 * Catches render errors so one bad child (e.g. SVAR Gantt) cannot blank the whole page.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    const label = this.props.context || 'ErrorBoundary';
    // eslint-disable-next-line no-console
    console.error(label, error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  handleRetry = () => {
    this.setState({ error: null });
    this.props.onRetry?.();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (typeof this.props.fallback === 'function') {
      return this.props.fallback({ error, retry: this.handleRetry });
    }
    if (this.props.fallback != null) return this.props.fallback;

    return (
      <Box
        sx={{
          p: 2,
          border: '1px solid var(--color-border, #ddd)',
          borderRadius: 2,
          background: 'var(--color-bg-alt, #fff)',
          color: 'var(--color-text, #222)'
        }}
      >
        <Alert
          severity="error"
          sx={{
            backgroundColor: 'var(--color-error-light, rgba(244, 67, 54, 0.12))',
            color: 'var(--color-text, #222)',
            '& .MuiAlert-icon': { color: 'var(--color-error, #f44336)' },
            '& .MuiAlert-message': { color: 'var(--color-text, #222)' }
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={this.handleRetry}
              sx={{ color: 'var(--color-accent, inherit)', textTransform: 'none' }}
            >
              Retry
            </Button>
          }
        >
          {this.props.message || 'Something went wrong while rendering this section.'}
        </Alert>
      </Box>
    );
  }
}

export default ErrorBoundary;
